/**
 * StaffPOSGuidePage - Printable Step-by-Step POS Guide for Staff
 * 
 * URL: /staff-pos-guide
 * This page is designed to be printed as a training document.
 */
import { useEffect } from 'react';
import { 
  Coffee, Clock, User, ShoppingCart, CreditCard, Printer, 
  LogOut, AlertTriangle, CheckCircle, ArrowRight, Phone
} from 'lucide-react';

export default function StaffPOSGuidePage() {
  useEffect(() => {
    // Auto-open print dialog when page loads
    const timer = setTimeout(() => {
      // Don't auto-print, let user click the button
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Print Button - Hidden when printing */}
      <div className="print:hidden fixed top-4 right-4 z-50">
        <button
          onClick={handlePrint}
          className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:bg-teal-700 flex items-center gap-2"
        >
          <Printer className="w-5 h-5" />
          Print Guide
        </button>
      </div>

      {/* Header */}
      <div className="bg-teal-600 text-white py-8 px-6 print:py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Coffee className="w-12 h-12 print:w-8 print:h-8" />
          <div>
            <h1 className="text-3xl font-bold print:text-2xl">Bulaccino Cafe</h1>
            <p className="text-teal-100 text-lg print:text-base">Staff POS Terminal Guide</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 print:p-4">
        {/* Introduction */}
        <div className="mb-8 print:mb-4">
          <p className="text-lg text-gray-600 print:text-sm">
            This guide covers how to start your shift, process orders, handle breaks, and close your shift using the POS terminal.
          </p>
        </div>

        {/* Section 1: Starting Your Shift */}
        <section className="mb-10 print:mb-6 page-break-inside-avoid">
          <div className="flex items-center gap-3 mb-4 print:mb-2">
            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center print:w-8 print:h-8">
              <Clock className="w-6 h-6 text-teal-600 print:w-4 print:h-4" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 print:text-xl">1. Starting Your Shift</h2>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-6 print:p-4 print:bg-gray-100">
            <ol className="space-y-4 print:space-y-2">
              <li className="flex gap-3">
                <span className="font-bold text-teal-600 min-w-[24px]">1.</span>
                <div>
                  <p className="font-medium">Go to the POS terminal at <span className="font-mono bg-gray-200 px-2 py-0.5 rounded">/pos</span></p>
                  <p className="text-gray-600 text-sm print:text-xs">On the tablet, tap the POS icon on the home screen</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-teal-600 min-w-[24px]">2.</span>
                <div>
                  <p className="font-medium">Enter your 6-digit PIN</p>
                  <p className="text-gray-600 text-sm print:text-xs">Your manager will provide your unique PIN. Keep it private.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-teal-600 min-w-[24px]">3.</span>
                <div>
                  <p className="font-medium">Open Shift Screen will appear</p>
                  <p className="text-gray-600 text-sm print:text-xs">If a shift is already open by another staff member, you'll skip this step.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-teal-600 min-w-[24px]">4.</span>
                <div>
                  <p className="font-medium">Count the opening cash float</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    Standard float is <strong>$400 FJD</strong>. Use the quick button or count manually.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-teal-600 min-w-[24px]">5.</span>
                <div>
                  <p className="font-medium">Tap "Open Shift"</p>
                  <p className="text-gray-600 text-sm print:text-xs">You're now ready to take orders!</p>
                </div>
              </li>
            </ol>
          </div>

          {/* Important Note */}
          <div className="mt-4 bg-amber-50 border-l-4 border-amber-500 p-4 print:p-3">
            <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
              <AlertTriangle className="w-5 h-5 print:w-4 print:h-4" />
              <span>Important</span>
            </div>
            <p className="text-amber-800 text-sm print:text-xs">
              Only ONE shift can be open at a time. If the previous cashier didn't close their shift, contact your manager.
            </p>
          </div>
        </section>

        {/* Section 2: Taking Orders */}
        <section className="mb-10 print:mb-6 page-break-inside-avoid">
          <div className="flex items-center gap-3 mb-4 print:mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center print:w-8 print:h-8">
              <ShoppingCart className="w-6 h-6 text-green-600 print:w-4 print:h-4" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 print:text-xl">2. Processing a Sale</h2>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-6 print:p-4 print:bg-gray-100">
            <ol className="space-y-4 print:space-y-2">
              <li className="flex gap-3">
                <span className="font-bold text-green-600 min-w-[24px]">1.</span>
                <div>
                  <p className="font-medium">Customer Lookup (Optional)</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    Search by phone or email to apply loyalty discounts, or tap "Skip" for guest orders.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-green-600 min-w-[24px]">2.</span>
                <div>
                  <p className="font-medium">Add items to cart</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    Browse categories or use search. Tap an item to add it. Tap again to increase quantity.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-green-600 min-w-[24px]">3.</span>
                <div>
                  <p className="font-medium">Customize if needed</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    Select size, add-ons (extra shot, oat milk, etc.) when the customization popup appears.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-green-600 min-w-[24px]">4.</span>
                <div>
                  <p className="font-medium">Review the cart</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    Check quantities and totals. Remove items with the X button if needed.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-green-600 min-w-[24px]">5.</span>
                <div>
                  <p className="font-medium">Tap "Pay Now"</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    Select payment method: Cash, Card/EFTPOS, or M-PAiSA.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-green-600 min-w-[24px]">6.</span>
                <div>
                  <p className="font-medium">Complete the payment</p>
                  <ul className="text-gray-600 text-sm print:text-xs mt-1 space-y-1">
                    <li>• <strong>Cash:</strong> Enter amount received, system calculates change</li>
                    <li>• <strong>Card:</strong> Process on EFTPOS machine, tap "Confirm"</li>
                  </ul>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-green-600 min-w-[24px]">7.</span>
                <div>
                  <p className="font-medium">Receipt prints automatically</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    Hand receipt to customer. The order number appears on the kitchen display.
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        {/* Section 3: Handling Breaks */}
        <section className="mb-10 print:mb-6 page-break-inside-avoid">
          <div className="flex items-center gap-3 mb-4 print:mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center print:w-8 print:h-8">
              <User className="w-6 h-6 text-blue-600 print:w-4 print:h-4" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 print:text-xl">3. Taking a Break / Switching Staff</h2>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-6 print:p-4 print:bg-gray-100">
            <p className="mb-4 text-gray-700 print:text-sm">
              When you need a break or are switching with another cashier during the same shift:
            </p>
            <ol className="space-y-4 print:space-y-2">
              <li className="flex gap-3">
                <span className="font-bold text-blue-600 min-w-[24px]">1.</span>
                <div>
                  <p className="font-medium">Tap the Logout button (top right)</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    This logs YOU out but does NOT close the shift.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-blue-600 min-w-[24px]">2.</span>
                <div>
                  <p className="font-medium">The next staff member logs in with their PIN</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    They will continue on the same shift. No need to count cash again.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-blue-600 min-w-[24px]">3.</span>
                <div>
                  <p className="font-medium">When you return, log back in with your PIN</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    The shift remains open. Continue taking orders.
                  </p>
                </div>
              </li>
            </ol>
          </div>

          <div className="mt-4 bg-green-50 border-l-4 border-green-500 p-4 print:p-3">
            <div className="flex items-center gap-2 text-green-700 font-medium mb-1">
              <CheckCircle className="w-5 h-5 print:w-4 print:h-4" />
              <span>Remember</span>
            </div>
            <p className="text-green-800 text-sm print:text-xs">
              Logging out ≠ Closing shift. Multiple staff can take turns on the SAME shift throughout the day.
            </p>
          </div>
        </section>

        {/* Section 4: Ending Your Shift */}
        <section className="mb-10 print:mb-6 page-break-inside-avoid">
          <div className="flex items-center gap-3 mb-4 print:mb-2">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center print:w-8 print:h-8">
              <LogOut className="w-6 h-6 text-red-600 print:w-4 print:h-4" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 print:text-xl">4. Closing Your Shift (End of Day)</h2>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-6 print:p-4 print:bg-gray-100">
            <ol className="space-y-4 print:space-y-2">
              <li className="flex gap-3">
                <span className="font-bold text-red-600 min-w-[24px]">1.</span>
                <div>
                  <p className="font-medium">Tap the green "Shift Active" badge (top right)</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    This expands to show shift summary and "End Shift" button.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-red-600 min-w-[24px]">2.</span>
                <div>
                  <p className="font-medium">Tap "End Shift"</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    The cash count screen appears.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-red-600 min-w-[24px]">3.</span>
                <div>
                  <p className="font-medium">Count ALL cash in the drawer</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    Enter the count for each denomination ($100, $50, $20, etc.).
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-red-600 min-w-[24px]">4.</span>
                <div>
                  <p className="font-medium">Check for variance</p>
                  <ul className="text-gray-600 text-sm print:text-xs mt-1 space-y-1">
                    <li>• <strong>Expected:</strong> Opening float + Cash sales - Cash refunds</li>
                    <li>• <strong>Your Count:</strong> What you counted</li>
                    <li>• <strong>Variance:</strong> Should be $0.00 (green)</li>
                  </ul>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-red-600 min-w-[24px]">5.</span>
                <div>
                  <p className="font-medium">If there's a variance, explain it</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    Type a reason (e.g., "Gave wrong change on order #52").
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-red-600 min-w-[24px]">6.</span>
                <div>
                  <p className="font-medium">Enter Manager/Supervisor PIN</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    A manager must approve the shift closure.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-red-600 min-w-[24px]">7.</span>
                <div>
                  <p className="font-medium">Tap "Close Shift"</p>
                  <p className="text-gray-600 text-sm print:text-xs">
                    You'll be logged out. The shift is now closed.
                  </p>
                </div>
              </li>
            </ol>
          </div>

          <div className="mt-4 bg-red-50 border-l-4 border-red-500 p-4 print:p-3">
            <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
              <AlertTriangle className="w-5 h-5 print:w-4 print:h-4" />
              <span>End of Day Only</span>
            </div>
            <p className="text-red-800 text-sm print:text-xs">
              Only close the shift at the END of the business day, not during breaks. The next morning's cashier will open a new shift.
            </p>
          </div>
        </section>

        {/* Quick Reference */}
        <section className="mb-10 print:mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 print:text-xl print:mb-2">Quick Reference</h2>
          
          <div className="grid grid-cols-2 gap-4 print:gap-2">
            <div className="bg-teal-50 p-4 rounded-lg print:p-3">
              <h3 className="font-bold text-teal-800 mb-2 print:text-sm">Standard Float</h3>
              <p className="text-3xl font-bold text-teal-600 print:text-xl">$400 FJD</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg print:p-3">
              <h3 className="font-bold text-blue-800 mb-2 print:text-sm">Support</h3>
              <p className="text-lg text-blue-600 print:text-sm flex items-center gap-2">
                <Phone className="w-4 h-4" />
                675 0102
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-gray-200 pt-4 text-center text-gray-500 text-sm print:text-xs">
          <p>Bulaccino Cafe POS Staff Guide • Updated December 2024</p>
          <p className="mt-1">Questions? Ask your shift manager.</p>
        </footer>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 0.5in;
            size: A4;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .page-break-inside-avoid {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}
